import React, { useEffect, useRef, useState, useContext } from "react";
import type { ElementType, ReactNode } from "react";
import { useLocation, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../AuthProvider";
import { useTranslation } from "react-i18next";
import PageTemplate from "../components/PageTemplate";
import SlidePageTransition from "../components/transitions/SlidePageTransition";
import ErrorPage from "../components/ErrorPage";
import Spinner from "../components/Spinner";
import Modal from "../components/Modal";
import { resetAccount, resetPassword } from "../utils/parentService";
import { maskIban } from "../utils/ibanUtils";
import { fetchCurrentSchoolYear } from "../utils/fetchSchoolYears";
import useFormatCurrency from "../utils/useFormatCurrency";
import { approveBankAccount, createBankAccount } from "../utils/bankAccountService";
import http from "../api/apiClient"; // ✅ vervangt axios/config
import { formatDateDMY } from "../utils/dateUtils";
import { useCan } from "../authz/useCan";
import {
  categorizeChildClassrooms,
  resolveTeacherName,
  resolveTranslatedClassroomName,
} from "./Children/details/utils/childClassroomUtils";
import {
  deriveChildPlacementStatus,
  getPlacementStatusCopy,
} from "./Children/details/utils/childPlacementStatus";
import {
  navigateParentFlow,
  parentDetailsPath,
  parentEditPath,
  parentFlowTransitionState,
  parentOverviewPath,
} from "./parents/navigation";
import {
  getCachedParent,
  getCachedParentDetails,
  prefetchParentOverview,
  removeCachedParent,
  setCachedParentDetails,
  upsertCachedParent,
} from "./parents/cache";
import {
  FaCheck,
  FaChild,
  FaChevronDown,
  FaChevronRight,
  FaClipboardCheck,
  FaCopy,
  FaEdit,
  FaEnvelope,
  FaEllipsisV,
  FaHome,
  FaIdCard,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaPhone,
  FaShieldAlt,
  FaSchool,
  FaSyncAlt,
  FaTrash,
  FaUser,
  FaUserTie,
  FaUsers,
} from "react-icons/fa";
import type { ChildSummary, ClassroomSummary, FamilyParentLink, FamilySummary, Parent, StatusTone } from "./parents/types";

import BackButton, { useLeaveForm } from "../components/navigation/BackButton";
type PersonName = {
  firstName?: string | null;
  lastName?: string | null;
};

type SelectedPanel =
  | { type: "basic" }
  | { type: "families" }
  | { type: "children" }
  | { type: "family"; id: string }
  | { type: "child"; id: string }
  | null;

interface InfoModalState {
  show: boolean;
  title: string;
  message: string;
  copyableText?: string;
}

interface BankAccountFormData {
  iban: string;
  accountHolderName: string;
}

interface CreateParentBankAccountPayload extends BankAccountFormData {
  parentUuid: string;
  approvedBy?: string;
  approved?: boolean;
}

const fullName = (person: PersonName | null | undefined = {}) => {
  const safePerson = person || {};
  return [safePerson.firstName, safePerson.lastName].filter(Boolean).join(" ").trim();
};

const initialsFor = (name: string | null | undefined) =>
  String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join("") || "?";

const LdsStatusPill = ({ label, tone = "neutral", title }: { label: ReactNode; tone?: StatusTone | string; title?: string }) => {
  const tones: Record<StatusTone, string> = {
    success: "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-400/40 dark:bg-emerald-400/15 dark:text-emerald-100",
    warn: "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-400/40 dark:bg-amber-400/15 dark:text-amber-100",
    danger: "border-red-200 bg-red-50 text-red-800 dark:border-red-400/40 dark:bg-red-400/15 dark:text-red-100",
    info: "border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-400/40 dark:bg-sky-400/15 dark:text-sky-100",
    neutral: "border-slate-200 bg-slate-50 text-slate-700 dark:border-white/10 dark:bg-white/10 dark:text-slate-100",
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-bold ${tones[tone] || tones.neutral}`} title={title}>
      {label}
    </span>
  );
};

interface LdsStatCardProps {
  icon: ElementType;
  label: ReactNode;
  value: ReactNode;
  helper?: ReactNode;
  tone?: "indigo" | "emerald" | "sky" | "amber" | "violet";
  /** Maakt de hele tegel klikbaar (bijv. mailto: of tel:). */
  href?: string | null;
  /** Toont een kopieerknop die deze waarde naar het klembord kopieert. */
  copyValue?: string | null;
}

const LdsStatCard: React.FC<LdsStatCardProps> = ({
  icon: Icon,
  label,
  value,
  helper,
  tone = "indigo",
  href,
  copyValue,
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<number | null>(null);

  useEffect(() => () => {
    if (copiedTimer.current) window.clearTimeout(copiedTimer.current);
  }, []);

  const handleCopy = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!copyValue) return;
    try {
      await navigator.clipboard.writeText(copyValue);
      setCopied(true);
      if (copiedTimer.current) window.clearTimeout(copiedTimer.current);
      copiedTimer.current = window.setTimeout(() => setCopied(false), 1800);
    } catch (error) {
      console.error("Copy failed", error);
    }
  };

  const toneClasses: Record<"indigo" | "emerald" | "sky" | "amber" | "violet", string> = {
    indigo: "border-indigo-200/80 bg-indigo-50/55 text-indigo-600 dark:border-indigo-500/40 dark:bg-indigo-950/25 dark:text-indigo-200",
    emerald: "border-emerald-200/80 bg-emerald-50/55 text-emerald-600 dark:border-emerald-500/40 dark:bg-emerald-950/25 dark:text-emerald-200",
    sky: "border-sky-200/80 bg-sky-50/60 text-sky-600 dark:border-sky-500/40 dark:bg-sky-950/25 dark:text-sky-200",
    amber: "border-amber-200/80 bg-amber-50/60 text-amber-600 dark:border-amber-500/40 dark:bg-amber-950/25 dark:text-amber-200",
    violet: "border-violet-200/80 bg-violet-50/60 text-violet-600 dark:border-violet-500/40 dark:bg-violet-950/25 dark:text-violet-200",
  };
  const interactiveClasses = href
    ? "transition hover:-translate-y-0.5 hover:shadow-md focus-within:ring-2 focus-within:ring-indigo-400/60 motion-reduce:hover:translate-y-0"
    : "";
  const copyLabel = copied
    ? t("common.copied", { defaultValue: "Gekopieerd" })
    : t("common.actions.copy", { defaultValue: "Kopiëren" });

  return (
    <div className={`relative rounded-[18px] border p-4 shadow-sm ${toneClasses[tone] || toneClasses.indigo} ${interactiveClasses}`}>
      {href && (
        // Overlay-link: maakt de hele tegel klikbaar zonder de kopieerknop in een <a> te nesten.
        <a href={href} className="absolute inset-0 rounded-[18px] focus:outline-none" aria-label={`${typeof label === "string" ? label : ""} ${copyValue || ""}`.trim()} />
      )}
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/70 text-base shadow-sm dark:bg-white/10">
          <Icon aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold uppercase text-slate-500 dark:text-slate-400">{label}</p>
          <p className="mt-2 break-words text-base font-bold text-slate-950 dark:text-white">{value}</p>
          {helper && <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">{helper}</p>}
        </div>
        {copyValue && (
          <button
            type="button"
            onClick={handleCopy}
            className="relative z-10 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-indigo-700 shadow-sm transition hover:border-indigo-200 hover:text-indigo-900 dark:border-white/10 dark:bg-white/10 dark:text-indigo-200"
            aria-label={copyLabel}
            title={copyLabel}
          >
            {copied ? <FaCheck aria-hidden="true" /> : <FaCopy aria-hidden="true" />}
          </button>
        )}
      </div>
    </div>
  );
};

interface ParentTab {
  id: "info" | "families" | "children" | "finance" | "account";
  label: ReactNode;
}

const parentTabOrder: ParentTab["id"][] = ["info", "families", "children", "finance", "account"];

const LdsTabs = ({
  tabs,
  activeTab,
  onChange,
}: {
  tabs: ParentTab[];
  activeTab: ParentTab["id"];
  onChange: (tab: ParentTab["id"]) => void;
}) => {
  const icons: Record<ParentTab["id"], ElementType> = {
    info: FaUser,
    families: FaUsers,
    children: FaChild,
    finance: FaMoneyBillWave,
    account: FaShieldAlt,
  };
  return (
    <div className="overflow-hidden rounded-[24px] border border-slate-200/80 bg-white p-1.5 shadow-sm dark:border-white/10 dark:bg-white/5" role="tablist">
      <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-5">
        {tabs.map((tab) => {
          const Icon = icons[tab.id] || FaUser;
          const selected = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => onChange(tab.id)}
              className={`relative inline-flex min-h-[68px] items-center justify-center gap-3 rounded-[18px] px-4 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500 ${
                selected
                  ? "bg-indigo-50 text-indigo-700 shadow-sm ring-1 ring-indigo-100 dark:bg-indigo-400/15 dark:text-indigo-100 dark:ring-indigo-300/20"
                  : "text-slate-500 hover:bg-slate-50 hover:text-slate-800 dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-white"
              }`}
            >
              <Icon aria-hidden="true" />
              {tab.label}
              {selected && <span className="absolute inset-x-5 bottom-1.5 h-1 rounded-full bg-indigo-600 dark:bg-indigo-300" />}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const DetailRow = ({ icon: Icon, label, value, href }: { icon?: ElementType; label: ReactNode; value?: ReactNode; href?: string | null }) => {
  const content = (
    <>
      {Icon && <Icon className="shrink-0 text-slate-500 dark:text-slate-400" aria-hidden="true" />}
      <span className="min-w-[110px] text-sm font-medium text-slate-600 dark:text-slate-300">{label}</span>
      <span className="ml-auto min-w-0 break-words text-right text-sm font-semibold text-slate-950 dark:text-white">{value || "-"}</span>
    </>
  );
  const className = "flex items-center gap-3 border-b border-slate-200/80 py-4 last:border-b-0 dark:border-white/10";
  return href && value ? (
    <a href={href} className={`${className} transition hover:text-indigo-700 dark:hover:text-indigo-200`}>
      {content}
    </a>
  ) : (
    <div className={className}>{content}</div>
  );
};

const getFamilyParentLinks = (family: FamilySummary | null | undefined): FamilyParentLink[] => {
  if (!family) return [];
  const parentFields: FamilyParentLink[] = [
    {
      id: family.parent1Id,
      firstName: family.parent1FirstName,
      lastName: family.parent1LastName,
      email: family.parent1Email,
      phone: family.parent1Phone,
    },
    {
      id: family.parent2Id,
      firstName: family.parent2FirstName,
      lastName: family.parent2LastName,
      email: family.parent2Email,
      phone: family.parent2Phone,
    },
  ];
  const parentList = Array.isArray(family.parents) ? family.parents : [];
  const normalized = [...parentFields, ...parentList].map((item) => {
    const name =
      fullName(item) ||
      [item?.firstName, item?.lastName].filter(Boolean).join(" ").trim() ||
      item?.name ||
      item?.fullName ||
      "";
    return {
      id: item?.id || item?.uuid || item?.parentId,
      name,
      email: item?.email,
      phone: item?.phone,
    };
  });
  return Array.from(
    new Map(
      normalized
        .filter((item) => item.id || item.name)
        .map((item) => [String(item.id || item.name), item])
    ).values()
  );
};

const resolveChildCurrentClassroom = (child: ChildSummary | null | undefined): ClassroomSummary | null => {
  if (!child) return null;
  const classrooms = Array.isArray(child.classrooms) ? child.classrooms : [];
  return (
    child.currentClassroom ??
    child.currentClassroomAssignment ??
    child.activeClassroom ??
    classrooms.find((classroom) => classroom?.current) ??
    child.classroom ??
    classrooms[0] ??
    null
  );
};

const ParentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const leaveForm = useLeaveForm();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { keycloak } = useContext(AuthContext);
  const { t } = useTranslation();
  const canDeleteParent = useCan("family.parents", "delete");
  const canCrudParents = useCan("family.parents", "crud");
  const canUpdateParents = useCan("family.parents", "update");
  const canEditParent = canCrudParents || canUpdateParents;
  const canReadBankAccounts = useCan("family.bank-accounts", "read");
  const canApproveBankAccounts = useCan("family.bank-accounts", "approve");
  const canManageBankAccounts = canApproveBankAccounts;
  const canReadSchoolYears = useCan("school.years", "read");
  const cachedDetails = id ? getCachedParentDetails(id) : null;
  const cachedParent = id ? getCachedParent(id) : null;

  const [parent, setParent] = useState<Parent | null>(() => cachedDetails?.parent ?? cachedParent);
  const [families, setFamilies] = useState<FamilySummary[]>(() => cachedDetails?.families ?? []);
  const [children, setChildren] = useState<ChildSummary[]>(() => cachedDetails?.children ?? []);
  // Deleted children are shown (greyed out) until Noctis removes them, but no longer count for the fee.
  const payingChildren = children.filter((child) => !child.deletionRequestedAt);
  const [childClassroomHistoryById, setChildClassroomHistoryById] = useState<Record<string, unknown[]>>({});
  const childClassroomHistoryRequestedRef = useRef<Record<string, boolean>>({});
  const [, setRefreshingStatus] = useState(false);
  const [dangerZoneVisible, setDangerZoneVisible] = useState(false);
  const [currentYearFee, setCurrentYearFee] = useState<number | null>(() => cachedDetails?.currentYearFee ?? null);
  const formatCurrency = useFormatCurrency();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<BankAccountFormData>({
    iban: "",
    accountHolderName: "",
  });
  const [copyingIban, setCopyingIban] = useState(false);
  const [copiedIban, setCopiedIban] = useState(false);

  const [loading, setLoading] = useState(() => !cachedDetails && !cachedParent);
  const [showInitialSpinner, setShowInitialSpinner] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<ParentTab["id"]>(() => {
    const tab = searchParams.get("tab") as ParentTab["id"] | null;
    return tab && parentTabOrder.includes(tab) ? tab : "info";
  });
  const [tabSlideDirection, setTabSlideDirection] = useState<"forward" | "back">("forward");
  const [selectedPanel, setSelectedPanel] = useState<SelectedPanel>(null);
  const [expandedSections, setExpandedSections] = useState({
    families: false,
    children: false,
  });
  const [infoModal, setInfoModal] = useState<InfoModalState>({ show: false, title: "", message: "" });
  const [showConfirm, setShowConfirm] = useState(false);
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const hasInitialParentRef = useRef(Boolean(cachedDetails || cachedParent));
  const initialCurrentYearFeeRef = useRef(cachedDetails?.currentYearFee ?? null);
  const requestedReturnTo = (location.state as { returnTo?: unknown } | null)?.returnTo;
  const returnTo =
    typeof requestedReturnTo === "string" &&
    /^\/family\/[^/]+\/details(?:\?tab=(?:info|finance))?$/.test(requestedReturnTo)
      ? requestedReturnTo
      : parentOverviewPath;

  useEffect(() => {
    const tab = searchParams.get("tab") as ParentTab["id"] | null;
    setActiveTab(tab && parentTabOrder.includes(tab) ? tab : "info");
  }, [searchParams]);

  useEffect(() => {
      const fetchParentDetails = async () => {
        try {
          if (!keycloak?.token) {
            setError(t("common.auth_required"));
            setLoading(false);
            return;
          }

          const bankPromise = canReadBankAccounts
            ? http
                .get(`/bank-accounts/by-parent/${id}`)
                .then((res) => res)
                .catch((err) => {
                  if (err?.response?.status === 404 || err?.response?.status === 403) {
                    return { data: null };
                  }
                  throw err;
                })
            : Promise.resolve({ data: null });

          const currentYearPromise = canReadSchoolYears
            ? fetchCurrentSchoolYear().catch((err) => {
                if (err?.response?.status === 403) return null;
                throw err;
              })
            : Promise.resolve(null);

          const [parentResponse, familiesResponse, currentYear, bankResponse] = await Promise.all([
            http.get(`/parents/${id}`),
            http.get(`/families`, { params: { parentId: id } }),
            currentYearPromise,
            bankPromise,
          ]);

          const parentData = parentResponse.data;
          parentData.bankAccount = bankResponse.data || null; // ✅ veilig toewijzen

          const familyData = Array.isArray(familiesResponse.data) ? familiesResponse.data : [];

          setParent(parentData);
          upsertCachedParent(parentData);
          setFamilies(familyData);
          if (familyData.length === 1) {
            setSelectedPanel({ type: "family", id: familyData[0].id });
            setExpandedSections((current) => ({ ...current, families: false }));
          }

          const allChildren = familyData.flatMap((fam) =>
            (fam.children || []).map((child) => ({ ...child, familyId: fam.id }))
          );
          const uniqueChildren = Array.from(new Map(allChildren.map((c) => [c.id, c])).values());
          setChildren(uniqueChildren);

          let nextCurrentYearFee = initialCurrentYearFeeRef.current;
          if (currentYear?.defaultMonthlyFee) {
            nextCurrentYearFee = Number(currentYear.defaultMonthlyFee);
            setCurrentYearFee(nextCurrentYearFee);
          }
          setCachedParentDetails(String(id), {
            parent: parentData,
            families: familyData,
            children: uniqueChildren,
            currentYearFee: nextCurrentYearFee,
          });
        } catch (err) {
          if (!hasInitialParentRef.current) setError(t("common.load_error"));
        } finally {
          setLoading(false);
        }
      };

      if (keycloak?.token) {
        void fetchParentDetails();
      }
    }, [id, keycloak?.token, t, canReadBankAccounts, canReadSchoolYears]);

  useEffect(() => {
    if (!loading) {
      setShowInitialSpinner(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setShowInitialSpinner(true), 300);
    return () => window.clearTimeout(timer);
  }, [loading]);

  // After deleting the parent: leave the deleted page without keeping it in the history.
  const navigateBackToOverview = () => {
    if (returnTo !== parentOverviewPath) {
      leaveForm(returnTo);
      return;
    }
    prefetchParentOverview();
    leaveForm(parentOverviewPath, { state: parentFlowTransitionState("back") });
  };

  const handleTabChange = (nextTab: ParentTab["id"]) => {
    if (nextTab === activeTab) return;
    const currentIndex = parentTabOrder.indexOf(activeTab);
    const nextIndex = parentTabOrder.indexOf(nextTab);
    setTabSlideDirection(nextIndex > currentIndex ? "forward" : "back");
    setActiveTab(nextTab);
    const nextSearchParams = new URLSearchParams(searchParams);
    if (nextTab === "info") nextSearchParams.delete("tab");
    else nextSearchParams.set("tab", nextTab);
    setSearchParams(nextSearchParams, { replace: true, state: location.state });
  };

  const openRelatedDetails = (path: string) =>
    navigate(path, {
      state: { returnTo: `/family/parent/${id}/details?tab=${activeTab}` },
    });

  const toggleForm = () => setShowForm(!showForm);
  const updateForm = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  const handleCopyIban = async () => {
    if (!canReadBankAccounts) return;
    try {
      setCopyingIban(true);
      setCopiedIban(false);
      const { data } = await http.get(`/bank-accounts/by-parent/${id}`);
      const iban = data?.iban;
      if (!iban) {
        throw new Error("No IBAN returned");
      }
      await navigator.clipboard.writeText(iban);
      setCopiedIban(true);
      window.setTimeout(() => setCopiedIban(false), 1800);
    } catch (err) {
      alert(t("finance.iban_copy_error", { defaultValue: "IBAN kopiëren mislukt" }));
    } finally {
      setCopyingIban(false);
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    try {
      const payload: CreateParentBankAccountPayload = {
        parentUuid: parent.id,
        iban: formData.iban,
        accountHolderName: formData.accountHolderName,
        approvedBy: keycloak.id,
        approved: true,
      };
      await createBankAccount(payload);

      // Refresh parent data
      const res = await http.get(`/parents/${parent.id}`);
      setParent(res.data);
      setShowForm(false);
      setFormData({ iban: "", accountHolderName: "" });
    } catch (err) {
      alert(t("finance.create_error", { defaultValue: "Aanmaken mislukt" }));
    }
  };

  const refreshStatus = async () => {
    try {
      setRefreshingStatus(true);
      const res = await http.get(`/parents/${id}`);
      setParent(res.data);
    } catch (e) {
      setError(t("common.refresh_failed"));
    } finally {
      setRefreshingStatus(false);
    }
  };

  const handleDeleteParent = async () => {
    try {
      if (!keycloak?.token) {
        setError(t("common.auth_required"));
        return;
      }
      setDeleting(true);
      await http.delete(`/parents/${id}`);
      if (id) removeCachedParent(id);
      navigateBackToOverview();
    } catch (err) {
      setError(t("family.parents.common.delete_failed"));
    } finally {
      setDeleting(false);
      setShowConfirm(false);
    }
  };

  const handleApprove = async (bankId) => {
    try {
      await approveBankAccount(bankId, keycloak.token, keycloak.tokenParsed.sub);
      // eventueel herladen indien gewenst
    } catch (err) {
      alert(t("finance.approve_error", { defaultValue: "Goedkeuren mislukt" }));
    }
  };

  const handleUnlinkKeycloakReferences = async () => {
    try {
      await http.post(`/parents/${id}/unlink-keycloak`);
      setInfoModal({
        show: true,
        title: t("common.modal.success"),
        message: t("family.parents.messages.keycloak_unlinked"),
      });
      await refreshStatus();
    } catch (err) {
      setError(t("family.parents.messages.keycloak_unlink_failed"));
    } finally {
      setShowUnlinkConfirm(false);
      setDangerZoneVisible(false);
    }
  };

  const cancelDelete = () => setShowConfirm(false);

  const handlePasswordReset = async () => {
    try {
      if (!keycloak?.token) {
        setError(t("common.auth_required"));
        return;
      }
      const res = await resetPassword(id);
      const newPassword = res.data.temporaryPassword || res.data;
      setInfoModal({
        show: true,
        title: t("common.modal.success"),
        message: t("family.parents.messages.password_reset_sent"),
        copyableText: newPassword,
      });
    } catch (err) {
      setError(t("family.parents.messages.password_reset_failed"));
    }
  };

  const handleAccountReset = async () => {
    try {
      if (!keycloak?.token) {
        setError(t("common.auth_required"));
        return;
      }
      await resetAccount(id);
      setInfoModal({
        show: true,
        title: t("common.modal.success"),
        message: t("family.parents.messages.account_reset_done"),
      });
    } catch (err) {
      setError(t("family.parents.messages.account_reset_failed"));
    }
  };

  const [recomputing, setRecomputing] = useState(false);

  const selectBasicPanel = () => {
    setSelectedPanel({ type: "basic" });
    setExpandedSections((current) => ({ ...current, families: false, children: false }));
  };

  const selectFamiliesPanel = () => {
    if (families.length === 1) {
      setSelectedPanel({ type: "family", id: families[0].id });
      setExpandedSections((current) => ({ ...current, families: false, children: false }));
      return;
    }
    if (families.length > 0) {
      setExpandedSections((current) => ({
        ...current,
        families: !current.families,
        children: false,
      }));
      return;
    }
    setSelectedPanel({ type: "families" });
    setExpandedSections((current) => ({ ...current, children: false }));
  };

  const selectFamilyPanel = (familyId) => {
    setSelectedPanel((current) =>
      current?.type === "family" && String(current.id) === String(familyId)
        ? null
        : { type: "family", id: familyId }
    );
    setExpandedSections((current) => ({ ...current, children: false }));
  };

  const selectChildPanel = (childId) => {
    setSelectedPanel((current) =>
      current?.type === "child" && String(current.id) === String(childId)
        ? null
        : { type: "child", id: childId }
    );
    setExpandedSections((current) => ({ ...current, families: false }));
  };

  const recomputeNewsletterForParent = async () => {
      if (!families?.length) {
        setInfoModal({
          show: true,
          title: t("common.modal.info"),
          message: "Geen families gevonden bij deze ouder.",
        });
        return;
      }
      try {
        setRecomputing(true);
        // 🔒 admin endpoint; jouw http-client heeft al /api als base
        await Promise.all(
          families.map(fam => http.post(`/admin/newsletter/recompute/family/${fam.id}`))
        );
        // daarna ouder opnieuw ophalen
        await refreshStatus();
        setInfoModal({
          show: true,
          title: t("common.modal.success"),
          message: "Nieuwsbriefstatus herberekend.",
        });
      } catch (e) {
        setInfoModal({
          show: true,
          title: t("common.modal.error"),
          message: "Herberekenen mislukt.",
        });
  } finally {
        setRecomputing(false);
      }
  };

  useEffect(() => {
    if (activeTab === "finance" && !canReadBankAccounts && !canReadSchoolYears) {
      setActiveTab("info");
    }
    if (activeTab === "account" && !canEditParent) {
      setActiveTab("info");
    }
  }, [activeTab, canEditParent, canReadBankAccounts, canReadSchoolYears]);

  const selectedChildIdForHistory = selectedPanel?.type === "child" ? selectedPanel.id : null;

  useEffect(() => {
    if (!selectedChildIdForHistory) return undefined;
    const key = String(selectedChildIdForHistory);
    if (childClassroomHistoryRequestedRef.current[key]) return undefined;
    childClassroomHistoryRequestedRef.current[key] = true;

    let cancelled = false;
    http
      .get(`/child-classrooms/classrooms-for-child/${selectedChildIdForHistory}`)
      .then(({ data }) => {
        if (cancelled) return;
        setChildClassroomHistoryById((current) => ({
          ...current,
          [key]: Array.isArray(data) ? data : [],
        }));
      })
      .catch(() => {
        if (cancelled) return;
        setChildClassroomHistoryById((current) => ({ ...current, [key]: [] }));
      });

    return () => {
      cancelled = true;
    };
  }, [selectedChildIdForHistory]);

  const tabs: ParentTab[] = [{ id: "info", label: t("family.parents.details.basic_info") }];
  if (canReadBankAccounts || canReadSchoolYears) {
    tabs.push({ id: "finance", label: t("family.parents.details.finance") });
  }
  if (canEditParent) {
    tabs.push({ id: "account", label: t("family.parents.details.account_management") });
  }

  const portalStatus = !parent?.accountEnabled
    ? { label: t("common.inactive", { defaultValue: "Inactive" }), tone: "danger", cardTone: "danger" }
    : parent?.emailVerified
    ? { label: t("common.active", { defaultValue: "Active" }), tone: "success", cardTone: "success" }
    : { label: t("common.pending", { defaultValue: "Pending" }), tone: "warn", cardTone: "warn" };

  const newsletterStatus = parent?.newsletterOptOut
    ? {
        label: t("family.parents.newsletter.opted_out", { defaultValue: "Opted out" }),
        tone: "warn",
        cardTone: "warn",
      }
    : parent?.newsletterStatus === "SUBSCRIBED"
    ? {
        label: t("family.parents.newsletter.subscribed", { defaultValue: "Subscribed" }),
        tone: "success",
        cardTone: "success",
      }
    : parent?.newsletterStatus === "UNSUBSCRIBED"
    ? {
        label: t("family.parents.newsletter.unsubscribed", { defaultValue: "Unsubscribed" }),
        tone: "warn",
        cardTone: "warn",
      }
    : {
        label: t("family.parents.newsletter.ineligible", { defaultValue: "Ineligible" }),
      tone: "neutral",
      cardTone: "neutral",
    };

  const newsletterTitle = parent?.newsletterOptOut
    ? t("family.parents.newsletter.opted_out_detail", { defaultValue: "Ouder heeft zich afgemeld" })
    : parent?.newsletterStatus || t("family.parents.newsletter.ineligible", { defaultValue: "Ineligible" });

  if (loading) return showInitialSpinner ? <Spinner /> : null;
  if (error) return <ErrorPage message={error} status={undefined} onRetry={() => window.location.reload()} />;
  if (!parent) return <p>{t("family.parents.details.not_found")}</p>;

  const familiesCardTheme = {
    border: "border-sky-100/70 dark:border-sky-500/40",
    background: "bg-gradient-to-br from-sky-50/80 via-white to-white dark:from-sky-900/30 dark:via-gray-900/50 dark:to-gray-900/60",
  };
  const childrenCardTheme = {
    border: "border-indigo-100/70 dark:border-indigo-500/40",
    background: "bg-gradient-to-br from-indigo-50/80 via-white to-white dark:from-indigo-950/30 dark:via-gray-900/50 dark:to-gray-900/60",
  };
  const viewLabel = t("common.actions.view", { defaultValue: "View" });
  const parentName = fullName(parent);
  const notAvailable = t("common.not_available", { defaultValue: "Not available" });
  const selectedFamily =
    selectedPanel?.type === "family"
      ? families.find((family) => String(family.id) === String(selectedPanel.id)) || null
      : null;
  const selectedChild =
    selectedPanel?.type === "child"
      ? children.find((child) => String(child.id) === String(selectedPanel.id)) || null
      : null;
  const selectedChildHistory = selectedChild
    ? childClassroomHistoryById[String(selectedChild.id)] || []
    : [];
  const selectedChildClassroomGroups = selectedChild
    ? categorizeChildClassrooms(selectedChild, selectedChildHistory)
    : { currentClassrooms: [], futureClassrooms: [] };
  const selectedChildPlacement = selectedChild
    ? getPlacementStatusCopy(
        deriveChildPlacementStatus(selectedChild, selectedChildClassroomGroups),
        t
      )
    : null;
  const selectedChildClassroom =
    selectedChildClassroomGroups.currentClassrooms[0] ||
    selectedChildClassroomGroups.futureClassrooms[0] ||
    resolveChildCurrentClassroom(selectedChild);
  const selectedChildClassroomName =
    (selectedChildClassroom ? resolveTranslatedClassroomName(selectedChildClassroom) : "") ||
    selectedChild?.currentClassroomName ||
    selectedChild?.classroomName ||
    selectedChild?.className ||
    notAvailable;
  const selectedChildTeacherName =
    resolveTeacherName(selectedChildClassroom) ||
    selectedChild?.teacherName ||
    selectedChild?.classroom?.teacherName ||
    notAvailable;
  const selectedBasic = selectedPanel?.type === "basic";
  const singleFamilySelected = families.length === 1 && Boolean(selectedFamily);
  const selectedFamilyChildren = selectedFamily
    ? children.filter((child) => String(child.familyId) === String(selectedFamily.id))
    : [];
  const selectedFamilyPartner = selectedFamily
    ? getFamilyParentLinks(selectedFamily).find((item) => String(item.id || "") !== String(parent.id || id)) || null
    : null;
  const parentStats: LdsStatCardProps[] = [
    {
      icon: FaUsers,
      label: t("family.parents.details.assigned_families", { defaultValue: "Families" }),
      value: families.length,
      helper: t("family.parents.details.family_count_helper", { defaultValue: "Linked families" }),
      tone: "indigo",
    },
    {
      icon: FaEnvelope,
      label: t("profile.email", { defaultValue: "Email" }),
      value: parent.email || notAvailable,
      helper: t("family.parents.details.primary_email", { defaultValue: "Primary email" }),
      tone: "sky",
      href: parent.email ? `mailto:${parent.email}` : null,
      copyValue: parent.email || null,
    },
    {
      icon: FaPhone,
      label: t("family.parents.common.phone", { defaultValue: "Phone" }),
      value: parent.phone || notAvailable,
      helper: t("family.parents.details.mobile_phone", { defaultValue: "Mobile" }),
      tone: "violet",
      href: parent.phone ? `tel:${String(parent.phone).replace(/\s+/g, "")}` : null,
      copyValue: parent.phone ? String(parent.phone) : null,
    },
    {
      icon: FaHome,
      label: t("family.parents.common.address", { defaultValue: "Address" }),
      value: parent.address || notAvailable,
      helper: [parent.zipCode, parent.city].filter(Boolean).join(" ") || t("family.parents.details.home_address", { defaultValue: "Home address" }),
      tone: "amber",
    },
  ];
  return (
    <PageTemplate title={null} wrapperClassName="max-w-7xl mx-auto">
      <SlidePageTransition className="mx-auto rounded-[30px] border border-slate-200/80 bg-white/95 p-5 text-slate-900 shadow-[0_28px_90px_rgba(15,23,42,0.12)] backdrop-blur dark:border-white/10 dark:bg-slate-950/80 dark:text-slate-100 sm:p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <BackButton fallback={parentOverviewPath} />
          <div
            className="relative flex items-center gap-2"
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) {
                setActionsMenuOpen(false);
              }
            }}
          >
            {canEditParent && (
              <button
                type="button"
                onClick={() => navigateParentFlow(navigate, parentEditPath(String(id)), "forward")}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-200 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 motion-reduce:hover:translate-y-0 dark:border-white/10 dark:bg-white/10 dark:text-slate-100"
              >
                <FaEdit aria-hidden="true" />
                {t("common.actions.edit", { defaultValue: "Edit" })}
              </button>
            )}
            <button
              type="button"
              onClick={() => setActionsMenuOpen((open) => !open)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-indigo-200 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-white/10 dark:bg-white/10 dark:text-slate-200"
              aria-label={t("common.actions.more", { defaultValue: "More actions" })}
              aria-expanded={actionsMenuOpen}
              aria-haspopup="menu"
            >
              <FaEllipsisV aria-hidden="true" />
            </button>
            {actionsMenuOpen && (
              <div
                className="absolute right-0 top-14 z-30 min-w-44 rounded-2xl border border-slate-200 bg-white p-2 shadow-xl shadow-slate-900/10 dark:border-white/10 dark:bg-slate-900"
                role="menu"
              >
                {canDeleteParent ? (
                  <button
                    type="button"
                    onClick={() => {
                      setActionsMenuOpen(false);
                      setShowConfirm(true);
                    }}
                    disabled={deleting}
                    className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-semibold text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60 dark:text-red-200 dark:hover:bg-red-400/10"
                    role="menuitem"
                  >
                    <FaTrash aria-hidden="true" />
                    {deleting ? t("common.actions.deleting") : t("common.actions.delete", { defaultValue: "Delete" })}
                  </button>
                ) : (
                  <span className="block px-3 py-2 text-sm text-slate-500 dark:text-slate-400">
                    {t("common.no_actions", { defaultValue: "Geen acties" })}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-6">
          <header className="relative overflow-hidden rounded-[24px] border border-indigo-100/80 bg-gradient-to-r from-indigo-50/80 via-white to-sky-50/70 p-5 shadow-[0_18px_45px_rgba(67,56,202,0.12)] dark:border-indigo-400/25 dark:from-indigo-950/35 dark:via-slate-950 dark:to-sky-950/20 sm:p-6">
            <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-[42%] opacity-75 lg:block">
              <div className="absolute bottom-4 right-7 h-20 w-48 rounded-t-full bg-sky-100/75 dark:bg-sky-300/10" />
              <div className="absolute bottom-4 right-24 h-28 w-40 rounded-t-full bg-indigo-100/70 dark:bg-indigo-300/10" />
              <div className="absolute bottom-7 right-12 h-24 w-2 rounded-full bg-indigo-200/80 dark:bg-indigo-200/25" />
              <div className="absolute bottom-8 right-7 h-16 w-2 rounded-full bg-sky-200/80 dark:bg-sky-200/20" />
              <div className="absolute right-28 top-10 h-4 w-16 rounded-full bg-white/80 dark:bg-white/10" />
              <div className="absolute bottom-8 right-44 grid grid-cols-5 gap-2 opacity-55">
                {Array.from({ length: 25 }).map((_, index) => (
                  <span key={index} className="h-1.5 w-1.5 rounded-full bg-indigo-300/70 dark:bg-indigo-200/30" />
                ))}
              </div>
            </div>
            <div className="relative flex flex-col gap-5 md:flex-row md:items-center">
              <div className="relative flex h-28 w-28 shrink-0 items-center justify-center rounded-full border-[5px] border-white bg-indigo-50 text-3xl font-bold uppercase text-indigo-700 shadow-xl shadow-indigo-900/10 dark:border-white/20 dark:bg-indigo-400/15 dark:text-indigo-100 sm:h-32 sm:w-32">
                {initialsFor(parentName)}
              </div>
              <div className="min-w-0">
                <h2 className="min-w-0 break-words text-3xl font-bold text-slate-950 dark:text-white sm:text-4xl">
                  {parentName}
                </h2>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <LdsStatusPill label={portalStatus.label} tone={portalStatus.tone} />
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 dark:text-slate-300">
                    <FaUsers aria-hidden="true" className="text-indigo-600 dark:text-indigo-300" />
                    {t("family.parents.details.primary_contact", { defaultValue: "Primary contact" })}
                  </span>
                </div>
                <div className="mt-4 flex flex-wrap gap-3 text-sm font-medium text-slate-600 dark:text-slate-300">
                  <span className="inline-flex items-center gap-2">
                    <FaEnvelope aria-hidden="true" className="text-indigo-600 dark:text-indigo-300" />
                    {parent.email || notAvailable}
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <FaPhone aria-hidden="true" className="text-indigo-600 dark:text-indigo-300" />
                    {parent.phone || notAvailable}
                  </span>
                </div>
              </div>
            </div>
          </header>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {parentStats.map((stat) => (
              <LdsStatCard key={stat.label} {...stat} />
            ))}
          </div>

          <main className="space-y-7">
            <LdsTabs tabs={tabs} activeTab={activeTab} onChange={handleTabChange} />

            <div
              key={activeTab}
              className={`lumen-tab-slide lumen-tab-slide--${tabSlideDirection} text-sm text-slate-800 dark:text-slate-200`}
            >
            {activeTab === "info" && (
              <div className="rounded-[24px] border border-slate-200/80 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-white/5 sm:p-5">
                <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.92fr)]">
                  <div className="space-y-3">
                    <section className={`overflow-hidden rounded-[18px] border shadow-sm transition ${
                      selectedBasic
                        ? "border-indigo-400 bg-indigo-50/80 shadow-indigo-100 dark:border-indigo-300/40 dark:bg-indigo-400/15"
                        : "border-slate-200/80 bg-white dark:border-white/10 dark:bg-white/5"
                    }`}>
                      <button
                        type="button"
                        aria-pressed={selectedBasic}
                        onClick={selectBasicPanel}
                        className="flex w-full items-center gap-4 rounded-[18px] p-4 text-left transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500 dark:hover:bg-white/10"
                      >
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-400/15 dark:text-indigo-200">
                          <FaUser aria-hidden="true" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-base font-bold text-slate-950 dark:text-white">
                            {t("family.parents.details.basic_info", { defaultValue: "Personal details" })}
                          </span>
                          <span className="mt-1 block text-sm text-slate-500 dark:text-slate-300">
                            {t("family.parents.details.basic_info_summary", {
                              defaultValue: "Naam en contactgegevens",
                            })}
                          </span>
                        </span>
                        <FaChevronRight className="shrink-0 text-slate-400" aria-hidden="true" />
                      </button>
                    </section>

                    <section className={`overflow-hidden rounded-[18px] border shadow-sm transition ${
                      singleFamilySelected
                        ? "border-indigo-400 bg-indigo-50/80 shadow-indigo-100 dark:border-indigo-300/40 dark:bg-indigo-400/15"
                        : "border-slate-200/80 bg-white dark:border-white/10 dark:bg-white/5"
                    }`}>
                      <button
                        type="button"
                        aria-expanded={expandedSections.families}
                        onClick={selectFamiliesPanel}
                        className="flex w-full items-center gap-4 rounded-[18px] p-4 text-left transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500 dark:hover:bg-white/10"
                      >
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-400/15 dark:text-indigo-200">
                          <FaUsers aria-hidden="true" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-base font-bold text-slate-950 dark:text-white">
                            {t("family.parents.details.assigned_families", { defaultValue: "Families" })}
                          </span>
                          <span className="mt-1 block text-sm text-slate-500 dark:text-slate-300">
                            {t("family.parents.details.family_count_helper", {
                              defaultValue: "{{count}} linked families",
                              count: families.length,
                            })}
                          </span>
                        </span>
                        {families.length > 1 ? (
                          <FaChevronDown
                            className={`shrink-0 text-slate-400 transition ${expandedSections.families ? "rotate-180" : ""}`}
                            aria-hidden="true"
                          />
                        ) : (
                          <FaChevronRight className="shrink-0 text-slate-400" aria-hidden="true" />
                        )}
                      </button>
                      {expandedSections.families && families.length > 1 && (
                        <div className="space-y-2 border-t border-slate-200/80 p-3 dark:border-white/10">
                          {families.length > 0 ? (
                            families.map((family) => {
                              const selected = selectedPanel?.type === "family" && String(selectedPanel.id) === String(family.id);
                              const kids = children.filter((child) => child.familyId === family.id);
                              return (
                                <div
                                  key={family.id}
                                  className={`flex items-center gap-3 rounded-[18px] border p-3 transition ${
                                    selected
                                      ? "border-indigo-400 bg-indigo-50/80 shadow-sm shadow-indigo-100 dark:border-indigo-300/40 dark:bg-indigo-400/15"
                                      : "border-sky-100 bg-sky-50/55 dark:border-sky-400/20 dark:bg-sky-400/10"
                                  }`}
                                >
                                  <button
                                    type="button"
                                    aria-pressed={selected}
                                    onClick={() => selectFamilyPanel(family.id)}
                                    className="flex min-w-0 flex-1 items-center gap-3 rounded-[14px] text-left transition hover:text-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:hover:text-indigo-200"
                                  >
                                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sm font-bold text-sky-700 dark:bg-sky-300/15 dark:text-sky-100">
                                      {initialsFor(family.familyName)}
                                    </span>
                                    <span className="min-w-0">
                                      <span className="block truncate text-sm font-bold text-slate-950 dark:text-white">{family.familyName}</span>
                                      <span className="text-xs text-slate-500 dark:text-slate-300">
                                        {t("family.parents.details.child_count", {
                                          defaultValue: "{{count}} children linked",
                                          count: kids.length,
                                        })}
                                      </span>
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => openRelatedDetails(`/family/${family.id}/details`)}
                                    className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-sky-200 bg-white text-indigo-700 shadow-sm transition hover:-translate-y-0.5 hover:text-indigo-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 motion-reduce:hover:translate-y-0 dark:border-white/10 dark:bg-white/10 dark:text-indigo-200"
                                    aria-label={t("children.details.more_family_info", { defaultValue: "More family information" })}
                                  >
                                    <FaChevronRight aria-hidden="true" />
                                  </button>
                                </div>
                              );
                            })
                          ) : (
                            <button
                              type="button"
                              onClick={() => setSelectedPanel({ type: "families" })}
                              className="w-full rounded-2xl border border-dashed border-slate-200 p-4 text-left text-sm text-slate-500 transition hover:border-indigo-200 hover:text-indigo-700 dark:border-white/10 dark:text-slate-400 dark:hover:text-indigo-200"
                            >
                              {t("family.parents.details.none_assigned")}
                            </button>
                          )}
                        </div>
                      )}
                    </section>

                    <section className="overflow-hidden rounded-[18px] border border-slate-200/80 bg-white shadow-sm dark:border-white/10 dark:bg-white/5">
                      <button
                        type="button"
                        aria-expanded={expandedSections.children}
                        onClick={() =>
                          children.length > 0
                            ? setExpandedSections((current) => ({
                                ...current,
                                children: !current.children,
                                families: false,
                              }))
                            : (() => {
                                setSelectedPanel({ type: "children" });
                                setExpandedSections((current) => ({ ...current, families: false }));
                              })()
                        }
                        className="flex w-full items-center gap-4 rounded-[18px] p-4 text-left transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500 dark:hover:bg-white/10"
                      >
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-400/15 dark:text-indigo-200">
                          <FaChild aria-hidden="true" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-base font-bold text-slate-950 dark:text-white">
                            {t("family.parents.details.assigned_children", { defaultValue: "Children" })}
                          </span>
                          <span className="mt-1 block text-sm text-slate-500 dark:text-slate-300">
                            {t("family.parents.details.children_helper", {
                              defaultValue: "{{count}} linked children",
                              count: children.length,
                            })}
                          </span>
                        </span>
                        {children.length > 0 ? (
                          <FaChevronDown
                            className={`shrink-0 text-slate-400 transition ${expandedSections.children ? "rotate-180" : ""}`}
                            aria-hidden="true"
                          />
                        ) : (
                          <FaChevronRight className="shrink-0 text-slate-400" aria-hidden="true" />
                        )}
                      </button>
                      {expandedSections.children && (
                        <div className="space-y-2 border-t border-slate-200/80 p-3 dark:border-white/10">
                          {children.length > 0 ? (
                            children.map((child) => {
                              const childName = `${child.firstName || ""} ${child.lastName || ""}`.trim();
                              const selected = selectedPanel?.type === "child" && String(selectedPanel.id) === String(child.id);
                              const deleted = Boolean(child.deletionRequestedAt);
                              return (
                                <div
                                  key={child.id}
                                  data-deleted={deleted ? "true" : undefined}
                                  title={deleted ? t("family.details.child_deleted_title", { defaultValue: "Deleted; the child is removed for good once the retention period has passed" }) : undefined}
                                  className={`flex items-center gap-3 rounded-[18px] border p-3 transition ${deleted ? "opacity-60" : ""} ${
                                    selected
                                      ? "border-indigo-400 bg-indigo-50/80 shadow-sm shadow-indigo-100 dark:border-indigo-300/40 dark:bg-indigo-400/15"
                                      : "border-violet-100 bg-violet-50/60 dark:border-violet-400/20 dark:bg-violet-400/10"
                                  }`}
                                >
                                  <button
                                    type="button"
                                    aria-pressed={selected}
                                    onClick={() => selectChildPanel(child.id)}
                                    className="flex min-w-0 flex-1 items-center gap-3 rounded-[14px] text-left transition hover:text-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:hover:text-indigo-200"
                                  >
                                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-violet-100 text-xs font-bold text-violet-700 dark:bg-violet-300/15 dark:text-violet-100">
                                      {initialsFor(childName)}
                                    </span>
                                    <span className="min-w-0">
                                      <span className={`block truncate text-sm font-bold ${deleted ? "text-slate-400 line-through" : "text-slate-950 dark:text-white"}`}>{childName}</span>
                                      <span className="text-xs text-slate-500 dark:text-slate-300">
                                        {families.find((family) => family.id === child.familyId)?.familyName || ""}
                                        {deleted && (
                                          <span className="ml-2 inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-bold text-rose-700 no-underline dark:border-rose-400/30 dark:bg-rose-400/10 dark:text-rose-200">
                                          {t("family.details.child_deleted", { defaultValue: "Deleted" })}
                                        </span>
                                        )}
                                      </span>
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => openRelatedDetails(`/family/children/${child.id}/details`)}
                                    className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-violet-200 bg-white text-indigo-700 shadow-sm transition hover:-translate-y-0.5 hover:text-indigo-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 motion-reduce:hover:translate-y-0 dark:border-white/10 dark:bg-white/10 dark:text-indigo-200"
                                    aria-label={t("children.details.title", { defaultValue: "Child details" })}
                                  >
                                    <FaChevronRight aria-hidden="true" />
                                  </button>
                                </div>
                              );
                            })
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedPanel({ type: "children" });
                                setExpandedSections((current) => ({ ...current, families: false }));
                              }}
                              className="w-full rounded-2xl border border-dashed border-slate-200 p-4 text-left text-sm text-slate-500 transition hover:border-indigo-200 hover:text-indigo-700 dark:border-white/10 dark:text-slate-400 dark:hover:text-indigo-200"
                            >
                              {t("family.parents.details.no_children_assigned")}
                            </button>
                          )}
                        </div>
                      )}
                    </section>
                  </div>

                  <aside className="rounded-[18px] border border-slate-200/80 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-white/5">
                    {selectedBasic ? (
                      <>
                        <div className="mb-5 flex items-center gap-3">
                          <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-400/15 dark:text-indigo-200">
                            <FaUser aria-hidden="true" />
                          </span>
                          <div>
                            <h3 className="text-xl font-bold text-slate-950 dark:text-white">
                              {t("family.parents.details.basic_info", { defaultValue: "Personal details" })}
                            </h3>
                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">
                            {t("family.parents.details.basic_info_summary", {
                                defaultValue: "Naam en contactgegevens",
                              })}
                            </p>
                          </div>
                        </div>
                        <div className="rounded-[18px] border border-indigo-100 bg-indigo-50/55 p-4 dark:border-indigo-400/20 dark:bg-indigo-400/10">
                          <DetailRow icon={FaUser} label={t("common.given_name", { defaultValue: "First name" })} value={parent.firstName || notAvailable} />
                          <DetailRow icon={FaIdCard} label={t("common.family_name", { defaultValue: "Last name" })} value={parent.lastName || notAvailable} />
                          <DetailRow icon={FaEnvelope} label={t("profile.email", { defaultValue: "Email" })} value={parent.email || notAvailable} href={parent.email ? `mailto:${parent.email}` : null} />
                          <DetailRow icon={FaPhone} label={t("family.parents.common.phone", { defaultValue: "Phone" })} value={parent.phone || notAvailable} href={parent.phone ? `tel:${String(parent.phone).replace(/\s+/g, "")}` : null} />
                          <DetailRow icon={FaHome} label={t("family.parents.common.address", { defaultValue: "Address" })} value={parent.address || notAvailable} />
                          <DetailRow icon={FaMapMarkerAlt} label={t("family.parents.common.zip_code", { defaultValue: "Zip code" })} value={parent.zipCode || notAvailable} />
                          <DetailRow icon={FaMapMarkerAlt} label={t("family.parents.common.city", { defaultValue: "City" })} value={parent.city || notAvailable} />
                          <DetailRow
                            icon={FaShieldAlt}
                            label={t("family.parents.details.portal_status", { defaultValue: "Portal" })}
                            value={<LdsStatusPill label={portalStatus.label} tone={portalStatus.tone} />}
                          />
                          <DetailRow
                            icon={FaEnvelope}
                            label={t("family.parents.details.newsletter_status", { defaultValue: "Newsletter" })}
                            value={<LdsStatusPill label={newsletterStatus.label} tone={newsletterStatus.tone} title={newsletterTitle} />}
                          />
                          {canReadBankAccounts && (
                            <DetailRow
                              icon={FaMoneyBillWave}
                              label={t("finance.registered_account", { defaultValue: "Bank account" })}
                              value={parent.bankAccount ? maskIban(parent.bankAccount.iban) : t("finance.no_account", { defaultValue: "No account" })}
                            />
                          )}
                        </div>
                      </>
                    ) : selectedPanel?.type === "families" ? (
                      <>
                        <div className="mb-5 flex items-center gap-3">
                          <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-400/15 dark:text-indigo-200">
                            <FaUsers aria-hidden="true" />
                          </span>
                          <h3 className="text-xl font-bold text-slate-950 dark:text-white">
                            {t("family.parents.details.assigned_families", { defaultValue: "Families" })}
                          </h3>
                        </div>
                        <div className="rounded-[18px] border border-dashed border-slate-200 bg-slate-50/70 p-5 text-sm text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-400">
                          {t("family.parents.details.none_assigned")}
                        </div>
                      </>
                    ) : selectedPanel?.type === "children" ? (
                      <>
                        <div className="mb-5 flex items-center gap-3">
                          <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-400/15 dark:text-indigo-200">
                            <FaChild aria-hidden="true" />
                          </span>
                          <h3 className="text-xl font-bold text-slate-950 dark:text-white">
                            {t("family.parents.details.assigned_children", { defaultValue: "Children" })}
                          </h3>
                        </div>
                        <div className="rounded-[18px] border border-dashed border-slate-200 bg-slate-50/70 p-5 text-sm text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-400">
                          {t("family.parents.details.no_children_assigned")}
                        </div>
                      </>
                    ) : selectedFamily ? (
                      <>
                        <div className="mb-5 flex items-center gap-3">
                          <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-400/15 dark:text-indigo-200">
                            <FaUsers aria-hidden="true" />
                          </span>
                          <div className="min-w-0">
                            <h3 className="text-xl font-bold text-slate-950 dark:text-white">{selectedFamily.familyName}</h3>
                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">
                              {t("family.parents.details.family_details", { defaultValue: "Family details" })}
                            </p>
                          </div>
                        </div>
                        <div className="rounded-[18px] border border-sky-100 bg-sky-50/55 p-4 dark:border-sky-400/20 dark:bg-sky-400/10">
                          <DetailRow icon={FaChild} label={t("family.parents.details.assigned_children", { defaultValue: "Children" })} value={selectedFamilyChildren.length} />
                          {selectedFamilyPartner && (
                            <button
                              type="button"
                              onClick={() =>
                                selectedFamilyPartner.id
                                  ? navigateParentFlow(navigate, parentDetailsPath(selectedFamilyPartner.id), "forward")
                                  : undefined
                              }
                              className="flex w-full items-center gap-3 border-b border-slate-200/80 py-4 text-left transition hover:text-indigo-700 disabled:cursor-default disabled:hover:text-inherit dark:border-white/10 dark:hover:text-indigo-200"
                              disabled={!selectedFamilyPartner.id}
                            >
                              <FaUser className="shrink-0 text-slate-500 dark:text-slate-400" aria-hidden="true" />
                              <span className="min-w-[110px] text-sm font-medium text-slate-600 dark:text-slate-300">
                                {t("family.details.partner", { defaultValue: "Partner" })}
                              </span>
                              <span className="ml-auto min-w-0 break-words text-right text-sm font-semibold text-slate-950 dark:text-white">
                                {selectedFamilyPartner.name || notAvailable}
                              </span>
                              {selectedFamilyPartner.id && <FaChevronRight className="shrink-0 text-slate-400" aria-hidden="true" />}
                            </button>
                          )}
                          <DetailRow icon={FaIdCard} label={t("family.details.marital_status", { defaultValue: "Marital status" })} value={selectedFamily.familySituation || notAvailable} />
                          <button
                            type="button"
                            onClick={() => openRelatedDetails(`/family/${selectedFamily.id}/details`)}
                            className="mt-4 inline-flex w-full items-center justify-center gap-3 rounded-full border border-sky-200 bg-white px-5 py-3 text-sm font-semibold text-indigo-700 shadow-sm transition hover:-translate-y-0.5 hover:text-indigo-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 motion-reduce:hover:translate-y-0 dark:border-white/10 dark:bg-white/10 dark:text-indigo-200"
                          >
                            {t("children.details.more_family_info", { defaultValue: "More family information" })}
                            <FaChevronRight aria-hidden="true" />
                          </button>
                        </div>
                      </>
                    ) : selectedChild ? (
                      <>
                        <div className="mb-5 flex items-center gap-3">
                          <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-400/15 dark:text-indigo-200">
                            <FaChild aria-hidden="true" />
                          </span>
                          <div className="min-w-0">
                            <h3 className="text-xl font-bold text-slate-950 dark:text-white">
                              {`${selectedChild.firstName || ""} ${selectedChild.lastName || ""}`.trim()}
                            </h3>
                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">
                              {t("family.parents.details.child_details", { defaultValue: "Child details" })}
                            </p>
                          </div>
                        </div>
                        <div className="rounded-[18px] border border-violet-100 bg-violet-50/55 p-4 dark:border-violet-400/20 dark:bg-violet-400/10">
                          <DetailRow
                            icon={FaClipboardCheck}
                            label={t("children.details.status", { defaultValue: "Placement status" })}
                            value={
                              selectedChildPlacement ? (
                                <LdsStatusPill
                                  label={selectedChildPlacement.label}
                                  tone={selectedChildPlacement.tone === "rose" ? "danger" : selectedChildPlacement.tone}
                                />
                              ) : (
                                notAvailable
                              )
                            }
                          />
                          <DetailRow
                            icon={FaSchool}
                            label={t("children.details.current_classroom", { defaultValue: "Current classroom" })}
                            value={selectedChildClassroomName}
                          />
                          <DetailRow
                            icon={FaUserTie}
                            label={t("children.details.teacher", { defaultValue: "Teacher" })}
                            value={selectedChildTeacherName}
                          />
                          <DetailRow icon={FaIdCard} label={t("children.common.date_of_birth", { defaultValue: "Date of birth" })} value={formatDateDMY(selectedChild.dateBirth) || notAvailable} />
                          <button
                            type="button"
                            onClick={() => openRelatedDetails(`/family/children/${selectedChild.id}/details`)}
                            className="mt-4 inline-flex w-full items-center justify-center gap-3 rounded-full border border-violet-200 bg-white px-5 py-3 text-sm font-semibold text-indigo-700 shadow-sm transition hover:-translate-y-0.5 hover:text-indigo-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 motion-reduce:hover:translate-y-0 dark:border-white/10 dark:bg-white/10 dark:text-indigo-200"
                          >
                            {t("children.details.title", { defaultValue: "Child details" })}
                            <FaChevronRight aria-hidden="true" />
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="mb-5 flex items-center gap-3">
                          <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-400/15 dark:text-indigo-200">
                            <FaEnvelope aria-hidden="true" />
                          </span>
                          <div>
                            <h3 className="text-xl font-bold text-slate-950 dark:text-white">
                              {t("family.parents.details.contact_details", { defaultValue: "Contact details" })}
                            </h3>
                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">
                              {t("family.parents.details.primary_contact", { defaultValue: "Primary contact" })}
                            </p>
                          </div>
                        </div>
                        <div className="rounded-[18px] border border-sky-100 bg-sky-50/55 p-4 dark:border-sky-400/20 dark:bg-sky-400/10">
                          <DetailRow icon={FaEnvelope} label={t("profile.email", { defaultValue: "Email" })} value={parent.email || notAvailable} href={parent.email ? `mailto:${parent.email}` : null} />
                          <DetailRow icon={FaPhone} label={t("family.parents.common.phone", { defaultValue: "Phone" })} value={parent.phone || notAvailable} href={parent.phone ? `tel:${String(parent.phone).replace(/\s+/g, "")}` : null} />
                          <DetailRow icon={FaHome} label={t("family.parents.common.address", { defaultValue: "Address" })} value={parent.address || notAvailable} />
                          <DetailRow icon={FaMapMarkerAlt} label={t("family.parents.common.city", { defaultValue: "City" })} value={[parent.zipCode, parent.city].filter(Boolean).join(" ") || notAvailable} />
                          <DetailRow
                            icon={FaShieldAlt}
                            label={t("family.parents.details.portal_status", { defaultValue: "Portal" })}
                            value={<LdsStatusPill label={portalStatus.label} tone={portalStatus.tone} />}
                          />
                          <DetailRow
                            icon={FaEnvelope}
                            label={t("family.parents.details.newsletter_status", { defaultValue: "Newsletter" })}
                            value={<LdsStatusPill label={newsletterStatus.label} tone={newsletterStatus.tone} title={newsletterTitle} />}
                          />
                          {!parent?.newsletterOptOut && parent?.newsletterStatus !== "SUBSCRIBED" && (
                            <button
                              onClick={recomputeNewsletterForParent}
                              disabled={recomputing || !families?.length}
                              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full border border-indigo-200 bg-white px-4 py-2.5 text-sm font-semibold text-indigo-700 shadow-sm transition hover:-translate-y-0.5 hover:text-indigo-900 disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:hover:translate-y-0 dark:border-white/10 dark:bg-white/10 dark:text-indigo-200"
                            >
                              <FaSyncAlt aria-hidden="true" />
                              {recomputing
                                ? t("common.working", { defaultValue: "Working..." })
                                : t("common.actions.refresh", { defaultValue: "Refresh" })}
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </aside>
                </div>

                <div className="mt-5 rounded-[18px] border border-indigo-100 bg-gradient-to-r from-indigo-50/80 via-white to-sky-50/60 p-5 shadow-sm dark:border-indigo-400/25 dark:from-indigo-950/25 dark:via-white/5 dark:to-sky-950/20">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 items-start gap-4">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-700 dark:bg-indigo-400/15 dark:text-indigo-200">
                        <FaShieldAlt aria-hidden="true" />
                      </span>
                      <div className="min-w-0">
                        <p className="font-bold text-slate-950 dark:text-white">
                          {t("common.next_step", { defaultValue: "Next step" })}
                        </p>
                        <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
                          {t("family.parents.details.next_step", {
                            defaultValue: "Controleer en werk de contactgegevens bij indien nodig.",
                          })}
                        </p>
                      </div>
                    </div>
                    <LdsStatusPill label={newsletterStatus.label} tone={newsletterStatus.tone} title={newsletterTitle} />
                  </div>
                </div>
              </div>
            )}

            {activeTab === "families" && (
              <div className="space-y-4">
                {families.length > 0 ? (
                  families.map((family) => {
                    const kids = children.filter((child) => child.familyId === family.id);
                    return (
                      <div
                        key={family.id}
                        className={`flex flex-col gap-3 rounded-2xl border p-4 shadow-sm ${familiesCardTheme.border} ${familiesCardTheme.background} sm:flex-row sm:items-center sm:justify-between`}
                      >
                        <div>
                          <p className="text-lg font-semibold text-gray-900 dark:text-white">{family.familyName}</p>
                          <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            {t("family.parents.details.child_count", {
                              defaultValue: "{{count}} children gekoppeld",
                              count: kids.length,
                            })}
                          </p>
                        </div>
                        <button
                          onClick={() => openRelatedDetails(`/family/${family.id}/details`)}
                          className="lumen-glow-sheen inline-flex items-center justify-center rounded-full border border-indigo-200/70 bg-indigo-50/80 px-5 py-2 text-sm font-semibold text-indigo-700 transition hover:text-indigo-900 dark:border-indigo-500/50 dark:bg-indigo-950/40 dark:text-indigo-200"
                        >
                          {viewLabel}
                        </button>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-gray-500 dark:text-gray-400">{t("family.parents.details.none_assigned")}</p>
                )}
              </div>
            )}

            {activeTab === "children" && (
              <div className="space-y-4">
                {families.length > 0 ? (
                  families.map((family) => {
                    const kids = children.filter((child) => child.familyId === family.id);
                    return (
                      <div
                        key={family.id}
                        className={`rounded-2xl border p-4 shadow-sm ${childrenCardTheme.border} ${childrenCardTheme.background}`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                            {family.familyName}
                          </h4>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {kids.length} {t("family.parents.details.children_short", { defaultValue: "kinderen" })}
                          </span>
                        </div>
                        {kids.length > 0 ? (
                          <ul className="mt-3 space-y-2">
                            {kids.map((child) => (
                              <li
                                key={child.id}
                                data-deleted={child.deletionRequestedAt ? "true" : undefined}
                                title={child.deletionRequestedAt ? t("family.details.child_deleted_title", { defaultValue: "Deleted; the child is removed for good once the retention period has passed" }) : undefined}
                                className={`flex items-center justify-between rounded-xl border border-white/70 bg-white/90 px-3 py-2 text-gray-800 shadow-sm dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-200 ${child.deletionRequestedAt ? "opacity-60" : ""}`}
                              >
                                <span>
                                  <span className={child.deletionRequestedAt ? "text-slate-400 line-through" : undefined}>
                                    {child.firstName} {child.lastName}
                                  </span>
                                  {child.deletionRequestedAt && (
                                    <span className="ml-2 inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-bold text-rose-700 dark:border-rose-400/30 dark:bg-rose-400/10 dark:text-rose-200">
                                      {t("family.details.child_deleted", { defaultValue: "Deleted" })}
                                    </span>
                                  )}
                                </span>
                                <button
                                  onClick={() => openRelatedDetails(`/family/children/${child.id}/details`)}
                                  className="text-sm font-semibold text-indigo-600 hover:text-indigo-800 dark:text-indigo-300 dark:hover:text-indigo-200"
                                >
                                  {viewLabel}
                                </button>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                            {t("family.parents.details.no_children_assigned")}
                          </p>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p className="text-gray-500 dark:text-gray-400">{t("family.parents.details.no_children_assigned")}</p>
                )}
              </div>
            )}

            {activeTab === "finance" && (
              <div className="grid gap-5 lg:grid-cols-[minmax(0,1.75fr)_minmax(280px,1fr)]">
                <div className="space-y-5">
                  {canReadBankAccounts && (
                    <section className="rounded-[18px] border border-slate-200/80 bg-white/90 p-5 shadow-sm dark:border-white/10 dark:bg-white/10">
                      <h4 className="text-base font-bold text-slate-950 dark:text-white">
                        {t("finance.registered_account")}
                      </h4>
                      {!parent.bankAccount ? (
                        <>
                          <p className="mt-4 text-sm text-slate-500 dark:text-slate-300">{t("finance.no_account")}</p>
                          {canManageBankAccounts && !showForm ? (
                            <button
                              className="mt-4 inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-5 py-2.5 text-sm font-semibold text-sky-700 transition hover:-translate-y-0.5 hover:text-sky-900 motion-reduce:hover:translate-y-0 dark:border-sky-400/30 dark:bg-sky-400/10 dark:text-sky-100"
                              onClick={toggleForm}
                            >
                              {t("finance.add_account")}
                            </button>
                          ) : canManageBankAccounts && showForm ? (
                            <form onSubmit={handleCreateAccount} className="mt-4 space-y-3">
                              <input
                                type="text"
                                placeholder={t("finance.iban")}
                                value={formData.iban}
                                onChange={(e) => updateForm("iban", e.target.value)}
                                className="lumen-glow-input w-full px-4 py-2 text-gray-900 dark:text-white"
                              />
                              <input
                                type="text"
                                placeholder={t("finance.account_holder")}
                                value={formData.accountHolderName}
                                onChange={(e) => updateForm("accountHolderName", e.target.value)}
                                className="lumen-glow-input w-full px-4 py-2 text-gray-900 dark:text-white"
                              />
                              <div className="flex flex-wrap gap-2">
                                <button
                                  type="submit"
                                  className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-5 py-2 text-sm font-semibold text-emerald-700 transition hover:-translate-y-0.5 hover:text-emerald-900 motion-reduce:hover:translate-y-0 dark:border-emerald-400/30 dark:bg-emerald-400/10 dark:text-emerald-100"
                                >
                                  {t("common.save")}
                                </button>
                                <button
                                  type="button"
                                  className="inline-flex items-center rounded-full border border-slate-200 bg-white px-5 py-2 text-sm font-semibold text-slate-700 transition hover:-translate-y-0.5 hover:text-slate-900 motion-reduce:hover:translate-y-0 dark:border-white/10 dark:bg-white/10 dark:text-slate-100"
                                  onClick={toggleForm}
                                >
                                  {t("common.cancel")}
                                </button>
                              </div>
                            </form>
                          ) : null}
                        </>
                      ) : (
                        <div className="mt-4 divide-y divide-slate-200/80 dark:divide-white/10">
                          <div className="flex items-center gap-3 py-4">
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-400/15 dark:text-indigo-200">
                              <FaMoneyBillWave aria-hidden="true" />
                            </span>
                            <span className="min-w-[110px] text-sm font-medium text-slate-600 dark:text-slate-300">
                              {t("finance.iban")}
                            </span>
                            <span className="ml-auto inline-flex flex-wrap items-center justify-end gap-2 text-right text-sm font-semibold text-slate-950 dark:text-white">
                              <span>{maskIban(parent.bankAccount.iban)}</span>
                              <button
                                type="button"
                                onClick={handleCopyIban}
                                disabled={copyingIban}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-indigo-700 shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-200 hover:text-indigo-900 disabled:cursor-wait disabled:opacity-60 motion-reduce:hover:translate-y-0 dark:border-white/10 dark:bg-white/10 dark:text-indigo-200"
                                aria-label={
                                  copiedIban
                                    ? t("common.copied", { defaultValue: "Gekopieerd" })
                                    : t("common.actions.copy", { defaultValue: "Kopiëren" })
                                }
                                title={
                                  copiedIban
                                    ? t("common.copied", { defaultValue: "Gekopieerd" })
                                    : t("common.actions.copy", { defaultValue: "Kopiëren" })
                                }
                              >
                                <FaCopy aria-hidden="true" />
                              </button>
                            </span>
                          </div>
                          <div className="flex items-center gap-3 py-4">
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-violet-50 text-violet-600 dark:bg-violet-400/15 dark:text-violet-200">
                              <FaUser aria-hidden="true" />
                            </span>
                            <span className="min-w-[110px] text-sm font-medium text-slate-600 dark:text-slate-300">
                              {t("finance.account_holder")}
                            </span>
                            <span className="ml-auto min-w-0 break-words text-right text-sm font-semibold text-slate-950 dark:text-white">
                              {parent.bankAccount.accountHolderName}
                            </span>
                          </div>
                          {canManageBankAccounts && parent.bankAccount.proposedIban && (
                            <div className="mt-4 rounded-[18px] border border-amber-200 bg-amber-50/80 p-4 text-sm text-amber-900 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-100">
                              <p>
                                <strong>{t("finance.proposed_iban")}:</strong> {maskIban(parent.bankAccount.proposedIban)}
                              </p>
                              <p>
                                <strong>{t("finance.proposed_account_holder")}:</strong>{" "}
                                {parent.bankAccount.proposedAccountHolderName}
                              </p>
                              <button
                                className="mt-3 inline-flex items-center rounded-full border border-emerald-200 bg-white px-4 py-2 text-xs font-semibold text-emerald-700 transition hover:-translate-y-0.5 hover:text-emerald-900 motion-reduce:hover:translate-y-0 dark:border-emerald-400/30 dark:bg-white/10 dark:text-emerald-100"
                                onClick={() => handleApprove(parent.bankAccount.id)}
                              >
                                {t("finance.approve")}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </section>
                  )}
                </div>

                <aside className="space-y-5">
                  {parent.bankAccount && (
                    <section className="rounded-[18px] border border-emerald-100 bg-gradient-to-br from-emerald-50/85 via-white to-emerald-50/55 p-5 shadow-sm dark:border-emerald-400/25 dark:from-emerald-950/30 dark:via-white/5 dark:to-emerald-950/20">
                      <div className="flex items-start gap-4">
                        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-100">
                          <FaShieldAlt aria-hidden="true" />
                        </span>
                        <div>
                          <p className="text-base font-bold text-emerald-800 dark:text-emerald-100">
                            {t("finance.preferred_active", { defaultValue: "Voorkeur actief" })}
                          </p>
                          <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
                            {t("finance.preferred_active_hint", {
                              defaultValue: "Deze bankrekening wordt gebruikt voor terugbetalingen en automatische incasso's.",
                            })}
                          </p>
                        </div>
                      </div>
                    </section>
                  )}

                  {canReadSchoolYears && (
                    <section className="rounded-[18px] border border-emerald-100 bg-gradient-to-br from-emerald-50/85 via-white to-white p-5 shadow-sm dark:border-emerald-400/25 dark:from-emerald-950/30 dark:via-white/5 dark:to-white/5">
                      <h4 className="text-base font-bold text-emerald-900 dark:text-emerald-100">
                        {t("finance.monthly_overview")}
                      </h4>
                      {payingChildren.length > 0 && currentYearFee != null ? (
                        <div className="mt-4 space-y-2 text-sm text-emerald-900 dark:text-emerald-100">
                          {payingChildren.map((child) => (
                            <div key={child.id} className="flex justify-between border-b border-emerald-200/70 pb-2 dark:border-emerald-400/20">
                              <span>{child.firstName}</span>
                              <span className="font-semibold">{formatCurrency(Number(child.monthlyContribution ?? currentYearFee))}</span>
                            </div>
                          ))}
                          <div className="flex justify-between pt-2 font-semibold">
                            <span>{t("finance.total")}</span>
                            <span>{formatCurrency(payingChildren.reduce((sum, child) => sum + Number(child.monthlyContribution ?? currentYearFee), 0))}</span>
                          </div>
                        </div>
                      ) : (
                        <p className="mt-4 text-sm text-amber-800 dark:text-amber-100">
                          {t("finance.no_fee_found")}
                        </p>
                      )}
                    </section>
                  )}
                </aside>
              </div>
            )}

            {activeTab === "account" && (
              <div className="space-y-5">
                <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
                  {t("family.parents.account.description")}
                </p>
                <div className="space-y-4">
                  <div className="flex flex-col gap-4 rounded-[18px] border border-slate-200/80 bg-white/90 p-5 shadow-sm dark:border-white/10 dark:bg-white/10 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 items-start gap-4">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-amber-50 text-amber-700 dark:bg-amber-400/15 dark:text-amber-100">
                        <FaShieldAlt aria-hidden="true" />
                      </span>
                      <div className="min-w-0">
                      <p className="text-base font-bold text-slate-950 dark:text-white">
                        {t("family.parents.account.reset_password_label") || "Reset the password:"}
                      </p>
                      <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">
                        {t("family.parents.account.reset_password_hint", {
                          defaultValue: "Genereert een tijdelijk wachtwoord voor deze ouder.",
                        })}
                      </p>
                      </div>
                    </div>
                    <button
                      className="inline-flex items-center justify-center rounded-full border border-amber-200 bg-amber-50 px-5 py-2.5 text-sm font-semibold text-amber-700 transition hover:-translate-y-0.5 hover:text-amber-900 motion-reduce:hover:translate-y-0 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-100"
                      onClick={handlePasswordReset}
                      type="button"
                    >
                      {t("family.parents.actions.reset_password")}
                    </button>
                  </div>

                  <div className="flex flex-col gap-4 rounded-[18px] border border-slate-200/80 bg-white/90 p-5 shadow-sm dark:border-white/10 dark:bg-white/10 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 items-start gap-4">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-red-50 text-red-600 dark:bg-red-400/15 dark:text-red-100">
                        <FaUser aria-hidden="true" />
                      </span>
                      <div className="min-w-0">
                      <p className="text-base font-bold text-slate-950 dark:text-white">
                        {t("family.parents.account.reset_account_label") || "Reset the account:"}
                      </p>
                      <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">
                        {t("family.parents.account.reset_account_hint", {
                          defaultValue: "Remove the login account and send a new activation email.",
                        })}
                      </p>
                      </div>
                    </div>
                    <button
                      className="inline-flex items-center justify-center rounded-full border border-red-200 bg-red-50 px-5 py-2.5 text-sm font-semibold text-red-600 transition hover:-translate-y-0.5 hover:text-red-800 motion-reduce:hover:translate-y-0 dark:border-red-400/30 dark:bg-red-400/10 dark:text-red-100"
                      onClick={handleAccountReset}
                      type="button"
                    >
                      {t("family.parents.actions.reset_account")}
                    </button>
                  </div>
                </div>

                <div
                  className="text-right text-sm font-medium text-red-500 underline-offset-2 transition hover:underline"
                  onClick={() => setDangerZoneVisible((prev) => !prev)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setDangerZoneVisible((prev) => !prev);
                    }
                  }}
                >
                  {t("family.parents.actions.unlink_keycloak_toggle") || "Problemen? Verwijder Keycloak link"}
                </div>

                {dangerZoneVisible && (
                  <div className="rounded-[18px] border border-red-200 bg-red-50/80 p-4 text-sm text-red-700 shadow-sm dark:border-red-400/30 dark:bg-red-400/10 dark:text-red-100">
                    <p className="mb-3">
                      {t("family.parents.account.unlink_warning") ||
                        "Dit verwijdert alleen de Keycloak koppeling in de database, niet het account."}
                    </p>
                    <button
                      onClick={() => setShowUnlinkConfirm(true)}
                      className="inline-flex items-center rounded-full border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-600 transition hover:-translate-y-0.5 hover:text-red-800 motion-reduce:hover:translate-y-0 dark:border-red-400/30 dark:bg-white/10 dark:text-red-100"
                    >
                      {t("family.parents.actions.unlink_keycloak")}
                    </button>
                  </div>
                )}
              </div>
            )}

          </div>
          </main>
        </div>
      </SlidePageTransition>

      {/* Bevestigingsmodal */}
      {canDeleteParent && (
        <Modal
          show={showConfirm}
          title={t("common.modal.are_you_sure")}
          message={t("common.modal.action_cannot_be_undone")}
          isConfirm={true}
          type="confirm"
          onConfirm={handleDeleteParent}
          onClose={cancelDelete}
        />
      )}
            <Modal
                show={infoModal.show}
                title={infoModal.title}
                message={infoModal.message}
                copyableText={infoModal.copyableText} // ✅ belangrijk!
                isConfirm={false}
                type="info"
                onClose={() =>
                    setInfoModal({
                        show: false,
                        title: "",
                        message: "",
                        copyableText: "", // ✅ reset mee
                    })
                }
            />
            <Modal
                show={showUnlinkConfirm}
                title={t("common.modal.are_you_sure")}
                message={t("family.parents.account.unlink_warning")}
                isConfirm={true}
                type="confirm"
                onConfirm={handleUnlinkKeycloakReferences}
                onClose={() => setShowUnlinkConfirm(false)}
            />
        </PageTemplate>
    );
};

export default ParentDetails;
